import React, { FC, useCallback, useEffect, useMemo } from 'react';
import {
  AdminLegendVariant,
  PharmacistLegendVariant,
  ProfessionalReplacementTypes,
  TypeOfBill,
  TypeOfSummary,
  adminActions,
  adminContractDetails,
  adminPharmacistResume,
  adminRequestContractDetails,
  adminRequestedDetails,
  getAdminPharmacistResumes,
  getRequestedDetails,
} from '@pharmaplan/common';
import { Box } from '@mui/system';
import { idStore } from '@pharmaplan/common/helpers/ServiceHandler';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import styles from './styles';
import ServiceCard from '../../../SelfService/ServiceCard';
import CustomGridLayout from '../../../common/CustomGridLayout';
import strings from '../../../../localization';
import PharmacistCard from '../../../SelfService/PharmacistCard';
import {
  Constants,
  OtherScreens,
  ReplacementLabels,
  ScreenTypes,
} from '../../../../helpers/Constants';
import SoftwareExpertise from '../../../SelfService/SoftwareExpertise';
import PharmacistSummary from '../../../SelfService/PharmacistSummary';
import BillDetails from '../../../SelfService/BillDetails';
import {
  renderScreen,
  setBack,
  setFooter,
  setFooterShow,
} from '../../../../actions/drawerActions';
import useRequestedPharmacistActions from '../../../../hooks/AvailableWorkshift/useRequestedPharmacistActions';
import { serverToday } from '../../../../helpers/Functions';
import useDetailsPagination from '../../../../hooks/Admin/useDetailsPagination';
import useGetItems from '../../../../hooks/useGetItems';
import useOptimizedAvatar from '../../../../hooks/useOptimizedAvatar';
import { getMultipleEventAdd } from '../../../../selectors/calendarSelector';
import ViewRequestedCounter from './ViewRequestedCounter';
import LongShiftsDistribution from '../../../SelfService/PharmacistProfile/LongShiftsDistribution';
import LongTermNote from '../../../SelfService/PharmacistProfile/LongTermNote';
import LongTermBillDetails from '../../../SelfService/LongTermBillDetails';
import ProfessionalExperience from '../../../SelfService/PharmacistProfile/ProfessionalExperience/ProfessionalExperience';

interface IViewRequestedDetails {
  bookingId: string;
  workshiftId: string;
  bookingIdList?: Array<string> | null;
  isAutoSyncRecord?: boolean;
  isExternalPharmacist?: boolean;
  longTermShiftContractId?: string;
  professionalId?: string;
}

const {
  adminPharmacistPictures: adminPharmacistPicturesAction,
  getRequestedDetails: getRequestedDetailsAction,
} = adminActions;

const {
  variant: { text },
} = Constants;

const ViewRequestedDetails: FC<IViewRequestedDetails> = ({
  bookingId,
  workshiftId,
  bookingIdList,
  professionalId,
  isAutoSyncRecord,
  isExternalPharmacist,
  longTermShiftContractId,
}) => {
  const dispatch = useAppDispatch();
  const resume = useAppSelector(adminPharmacistResume);

  const requestedDetails = useAppSelector(adminRequestedDetails);
  const contractDetails = useAppSelector(adminContractDetails);

  const enabledMultipleAdd = useAppSelector(getMultipleEventAdd);

  const { id, paginationModel } = useDetailsPagination(bookingIdList);
  const eventId = id ?? bookingId;
  const isNotFromMap = !id;

  const { ignoreRequestAction, goToApproveBooking } = useRequestedPharmacistActions(isAutoSyncRecord);

  const isPast = (serverToday().toISO({ includeOffset: false }) ?? '')
    > requestedDetails?.startDate;

  const { longTermShiftContract, counterShiftContract } = contractDetails ?? {};
  const { weekDayPaidHourlyRate, weekendPaidHourlyRate } = longTermShiftContract ?? {};
  const {
    weekDayPaidHourlyRate: counterWeekdayRate,
    weekendPaidHourlyRate: counterWeekendRate,
  } = counterShiftContract ?? {};

  const {
    experience,
    email,
    address,
    province,
    city,
    postalCode,
    replacementType,
    softwares,
    licenseNumber,
    counterOfferId,
  } = requestedDetails ?? {};

  const replacement = ReplacementLabels()[
    replacementType ?? ProfessionalReplacementTypes.pharmacist
  ];
  const userId = requestedDetails[
    idStore.id as keyof typeof requestedDetails
  ] as string;

  const goToRequestDetails = () => {
    dispatch(
      renderScreen({
        screenNumber: 2,
        screenType: ScreenTypes.requestedList,
        eventId: workshiftId,
        extraParameters: {
          isAutoSyncRecord,
        },
      }),
    );
  };

  const pharmacistDetailsConfig = [
    { key: strings.yearsOfExperience, value: experience?.toString() },
    { key: strings.licenseNo, value: licenseNumber },
    { key: strings.email, value: email },
    {
      key: strings.address,
      value: `${address}, ${city}, ${province}, ${postalCode}`,
    },
  ];

  const fullEvent = {
    ...requestedDetails,
    cost: requestedDetails.bookingCost,
  };

  const { getPharmacistImage } = useGetItems();

  const pharmacistDetails = {
    ...requestedDetails,
    profilePicture: getPharmacistImage(userId),
  };

  useEffect(() => {
    if (enabledMultipleAdd) {
      dispatch(setFooterShow(false));
      dispatch(setBack(null));
    }
    if (isNotFromMap) {
      dispatch(setBack(goToRequestDetails));
    }

    return () => {
      dispatch(setFooterShow(false));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workshiftId]);

  useEffect(() => {
    if (!isPast) {
      dispatch(
        setFooter({
          show: !isExternalPharmacist,
          pagination: isNotFromMap
            ? {
              show: false,
            }
            : paginationModel,
          buttons: {
            show: true,
            primaryButton: {
              label: strings.continue,
              onClick: goToApproveBooking({
                bookingId: eventId,
                workshiftId,
                fromScreen: isNotFromMap
                  ? OtherScreens.AdminCalendar
                  : OtherScreens.AdminMap,
                eventIds: bookingIdList,
                longTermShiftContractId,
                pharmacistId: userId,
              }),
            },
            secondaryButton: {
              variant: text,
              label: strings.ignore,
              onClick: ignoreRequestAction(eventId),
            },
          },
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPast, eventId]);

  useEffect(() => {
    if (professionalId && longTermShiftContractId) {
      dispatch(
        adminRequestContractDetails({
          longTermContractId: longTermShiftContractId,
          professionalId,
        }),
      );
    }
    dispatch(getRequestedDetails({ id: eventId, isAutoSyncRecord }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const pIds = useMemo(() =>
    [userId], [userId]);

  useEffect(() => {
    dispatch(getAdminPharmacistResumes(professionalId ?? ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useOptimizedAvatar(pIds);

  const SingleDayDetails = useCallback(
    () =>
      (counterOfferId ? (
        <ViewRequestedCounter event={fullEvent} />
      ) : (
        <>
          <PharmacistSummary
            event={requestedDetails}
            summaryType={TypeOfSummary.workshiftDetails}
            type={AdminLegendVariant.requested}
            isAutoSyncRecord={isAutoSyncRecord}
            isCounterOffer={!!counterOfferId}
          />
          <BillDetails
            isAutoSyncRecord={isAutoSyncRecord}
            successAction={getRequestedDetailsAction}
            billType={TypeOfBill.finalBill}
            event={fullEvent}
            customTitle={replacement}
            isCounterOffer={!!counterOfferId}
          />
        </>
      )),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [counterOfferId, isAutoSyncRecord, replacement, requestedDetails],
  );

  return (
    <Box>
      <PharmacistCard
        eventIds={bookingIdList ?? []}
        type={PharmacistLegendVariant.requested}
        workshiftId={workshiftId}
        eventId={eventId}
        successAction={getRequestedDetailsAction}
        pharmacistDetails={pharmacistDetails}
        screenType={ScreenTypes.pharmacistProfile}
        imageSuccessAction={adminPharmacistPicturesAction}
        showRating={!isAutoSyncRecord && !isExternalPharmacist}
        isExternalPharmacist={isAutoSyncRecord || isExternalPharmacist}
      />
      <ServiceCard
        customContainerStyle={styles.serviceContainer}
        content={(
          <CustomGridLayout
            successAction={getRequestedDetailsAction}
            title={strings.professionalDetails}
            config={pharmacistDetailsConfig}
          />
        )}
      />
      <ProfessionalExperience
        resume={resume}
        successAction={getRequestedDetailsAction}
        details={requestedDetails}
      />
      <SoftwareExpertise
        successAction={getRequestedDetailsAction}
        softwareDetails={softwares}
      />
      {longTermShiftContractId ? (
        <>
          <LongShiftsDistribution
            details={contractDetails}
            counterWeekdayRate={counterWeekdayRate}
            counterWeekendRate={counterWeekendRate}
            weekdayHourlyRate={weekDayPaidHourlyRate}
            weekendHourlyRate={weekendPaidHourlyRate}
            successAction={adminActions.adminRequestContractDetails}
          />
          <LongTermNote />
          <LongTermBillDetails
            details={contractDetails}
            successAction={adminActions.adminRequestContractDetails}
          />
        </>
      ) : (
        <SingleDayDetails />
      )}
    </Box>
  );
};

export default ViewRequestedDetails;
